// main.ts — application entry (plink-style note canvas, multiplayer service).
//
// Flow:
//   1. Show the entry overlay: pick a name, create or join a room.
//   2. On "enter" (a real gesture) → resume audio, set the URL to /r/<id>, boot.
//   3. Mount the full-screen plink playfield; connect to the relay; peers' plinks
//      bloom in their colour; the backing beat + clock are shared room state.
//
// The step-sequencer / loop-tile modules are retained for an opt-in "depth mode".

import * as Tone from "tone";
import { hueForUserVoice, isPlinkVoice, startAudio, voiceForUser, type PlinkVoiceId } from "./audio";
import { connectRoom, getOrCreateUserId, type SyncClient, type SyncEvent } from "./sync";
import { getStoredName, randomName, roomIdFromPath, setStoredName } from "./identity";
import { createPlinkField, type PlinkField } from "./plink";
import { createBeat, type BeatEngine } from "./beat";
import { mountControls } from "./ui/controls";
import { mountEntry } from "./ui/entry";
import { renderRoster, type RosterEntry } from "./ui/roster";
import { setRoomLabel, setStatus } from "./ui/visuals";

const userId = getOrCreateUserId();
let myName = getStoredName() || randomName();

function audioRunning(): boolean {
  return Tone.getContext().state === "running";
}

let unlocking = false;
async function unlockAudio(): Promise<void> {
  if (unlocking || audioRunning()) return;
  unlocking = true;
  try {
    await startAudio();
  } catch {
    /* retried on next gesture */
  } finally {
    unlocking = false;
  }
}

// -----------------------------------------------------------------------------
// Entry → boot
// -----------------------------------------------------------------------------

const entryRoot = document.getElementById("entry") as HTMLElement;

function wireShare(roomId: string): void {
  const btn = document.getElementById("share") as HTMLButtonElement | null;
  if (!btn) return;
  btn.hidden = false;
  btn.addEventListener("click", async () => {
    const url = location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "don't look at me", text: `join my room: ${roomId}`, url });
        return;
      }
    } catch { /* fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      btn.textContent = "copied!";
      setTimeout(() => { btn.textContent = "share"; }, 1500);
    } catch { /* ignore */ }
  });
}

mountEntry(entryRoot, {
  roomId: roomIdFromPath(),
  suggestedName: myName,
  onEnter: ({ name, roomId }) => {
    myName = name;
    setStoredName(name);
    if (roomIdFromPath() !== roomId) {
      history.replaceState({}, "", `/r/${encodeURIComponent(roomId)}`);
    }
    entryRoot.hidden = true;
    setRoomLabel(roomId);
    setStatus("connecting");
    wireShare(roomId);
    void unlockAudio(); // the enter click is our audio-unlock gesture
    boot(roomId);
  },
});

// Fallback: any later gesture also tries to unlock audio (iOS belt-and-braces).
// Self-removes once the context is running so it isn't a permanent listener.
function unlockOnGesture(): void {
  void unlockAudio().then(() => {
    if (audioRunning()) document.removeEventListener("pointerdown", unlockOnGesture);
  });
}
document.addEventListener("pointerdown", unlockOnGesture);

// -----------------------------------------------------------------------------
// Main app
// -----------------------------------------------------------------------------

let booted = false;
function boot(roomId: string): void {
  if (booted) return;
  booted = true;

  const fieldRoot = document.getElementById("field") as HTMLElement;
  const rosterRoot = document.getElementById("roster") as HTMLElement;
  const controlsRoot = document.getElementById("controls") as HTMLElement;
  const hint = document.getElementById("hint");
  let sync: SyncClient | null = null;
  let myVoice: PlinkVoiceId = voiceForUser(userId);
  let currentTempo = 110;

  let aligned = false; // transport phase-aligned to the relay clock once

  // roster: who's here + their instrument + name (self + peers)
  interface Peer { voice: PlinkVoiceId; name: string }
  const peers = new Map<string, Peer>();
  function peerFrom(uid: string, voice: unknown, name: unknown): Peer {
    return {
      voice: isPlinkVoice(voice) ? voice : voiceForUser(uid),
      name: typeof name === "string" && name ? name : "player",
    };
  }
  function drawRoster(): void {
    const entries: RosterEntry[] = [{ userId, voice: myVoice, name: myName, isYou: true }];
    for (const [uid, p] of peers) entries.push({ userId: uid, voice: p.voice, name: p.name, isYou: false });
    renderRoster(rosterRoot, entries);
  }

  const field: PlinkField = createPlinkField(fieldRoot, userId, (ev) => {
    hint?.classList.add("gone");
    sync?.send({ type: "plink", x: ev.x, y: ev.y, note: ev.note, voice: myVoice });
  });

  // Local transport; relay clock keeps tempo aligned across the room.
  Tone.getTransport().bpm.value = 110;
  Tone.getTransport().start("+0.1");

  const beat: BeatEngine = createBeat();

  function applyAccent(): void {
    controlsRoot.style.setProperty("--accent", `hsl(${hueForUserVoice(userId, myVoice)}, 85%, 60%)`);
  }

  function applyBeatState(on: boolean, groove: string): void {
    beat.setGroove(groove);
    if (on !== beat.isPlaying()) beat.toggle();
    controls.setBeatPlaying(beat.isPlaying());
    controls.setActiveGroove(groove);
  }

  const controls = mountControls(controlsRoot, {
    localUserId: userId,
    localHue: hueForUserVoice(userId, myVoice),
    currentVoice: myVoice,
    onVoice: (v) => {
      myVoice = v;
      field.setVoice(v);
      applyAccent();
      drawRoster();
      sync?.send({ type: "presence", voice: v, name: myName });
    },
    currentGroove: beat.currentGroove(),
    beatPlaying: beat.isPlaying(),
    currentTempo,
    onBeatToggle: () => sync?.send({ type: "beat", on: !beat.isPlaying(), groove: beat.currentGroove() }),
    onGroove: (id) => sync?.send({ type: "beat", on: beat.isPlaying(), groove: id }),
    onTempo: (bpm) => { currentTempo = bpm; sync?.send({ type: "tempo", bpm }); },
  });
  applyAccent();
  drawRoster();

  sync = connectRoom(roomId, { userId, instrument: "lead", voice: myVoice, name: myName });

  sync.on((ev: SyncEvent) => {
    setStatus(sync?.state() ?? "connecting");
    switch (ev.type) {
      case "snapshot": {
        Tone.getTransport().bpm.rampTo(ev.tempo, 0.5);
        currentTempo = ev.tempo;
        controls.setTempo(ev.tempo);
        for (const u of ev.users) {
          if (u.userId === userId) continue;
          peers.set(u.userId, peerFrom(u.userId, u.voice, u.name));
        }
        drawRoster();
        if (ev.beat) applyBeatState(ev.beat.on, ev.beat.groove);
        break;
      }
      case "clock": {
        const transport = Tone.getTransport();
        if (Math.abs(transport.bpm.value - ev.tempo) > 0.5) {
          transport.bpm.rampTo(ev.tempo, 0.25);
        }
        if (ev.tempo !== currentTempo) {
          currentTempo = ev.tempo;
          controls.setTempo(ev.tempo);
        }
        if (!aligned) {
          aligned = true;
          const ppq = transport.PPQ;
          const barTicks = ppq * 4;
          const targetPhase = ev.beat * (ppq / 4);
          const localPhase = transport.ticks % barTicks;
          let drift = targetPhase - localPhase;
          if (drift > barTicks / 2) drift -= barTicks;
          if (drift < -barTicks / 2) drift += barTicks;
          transport.ticks = transport.ticks + drift;
        }
        if (ev.beat === 0) field.pulse();
        break;
      }
      case "beat": {
        applyBeatState(ev.on, ev.groove);
        break;
      }
      case "userJoined": {
        if (ev.userId === userId) break;
        peers.set(ev.userId, peerFrom(ev.userId, ev.voice, ev.name));
        drawRoster();
        break;
      }
      case "userLeft": {
        peers.delete(ev.userId);
        drawRoster();
        break;
      }
      case "presence": {
        if (ev.userId === userId) break;
        peers.set(ev.userId, peerFrom(ev.userId, ev.voice, ev.name));
        drawRoster();
        break;
      }
      case "plink": {
        if (ev.userId === userId) break;
        const pv = isPlinkVoice(ev.voice) ? ev.voice : undefined;
        if (pv) {
          const existing = peers.get(ev.userId);
          if (!existing || existing.voice !== pv) {
            peers.set(ev.userId, { voice: pv, name: existing?.name ?? "player" });
            drawRoster();
          }
        }
        field.remote({ userId: ev.userId, x: ev.x, y: ev.y, note: ev.note, voice: pv });
        break;
      }
    }
  });
}
